<script setup lang="ts">
import { onBeforeUnmount, onMounted, ref } from "vue";
import * as Sentry from "@sentry/vue";

import { getFile } from "@/api/services/File";
import LoadingWrapper from "@/components/Reusables/Loading/LoadingWrapper/LoadingWrapper.vue";

// The PDF viewer used to draw its own full-screen backdrop and its own CloseButton on top of the
// page. Both the button and the iframe carried z-overlay and the iframe came later in DOM order, so
// the iframe painted over the button — and clicks and Escape inside a blob-document iframe never
// reach the parent document, so the modal had no way out at all. This is now a plain body component:
// ModalRoot owns the backdrop, the close button, the focus trap and Escape, and its close button sits
// on the dialog chrome above the iframe rather than underneath it.
const props = defineProps<{
  fileId: string;
}>();

const objectURL = ref<string>();
const isLoading = ref(true);
const hasError = ref(false);

onMounted(async () => {
  try {
    const response = await getFile(props.fileId);

    // getFile resolves to a { status, message } object instead of throwing when the request is
    // answered with a non-200, so a Blob is what tells us the file actually came back.
    if (!(response instanceof Blob)) throw new Error(`Could not load file ${props.fileId}`);

    objectURL.value = URL.createObjectURL(new Blob([response], { type: "application/pdf" }));
  } catch (error: unknown) {
    hasError.value = true;
    Sentry.captureException(error);
  } finally {
    isLoading.value = false;
  }
});

onBeforeUnmount(() => {
  if (objectURL.value) URL.revokeObjectURL(objectURL.value);
});
</script>

<template>
  <LoadingWrapper
    :loading="isLoading"
    :error="hasError">
    <iframe
      class="rounded-8 h-full w-full bg-slate-700 dark:bg-slate-100"
      :src="objectURL"
      title="EPD document"
      frameborder="0"></iframe>
  </LoadingWrapper>
</template>
